/**
 * iter 66 — UNIFIED APPSHELL & PAGE LIFECYCLE.
 *
 * Five primitives that every page in the app should be built on top of:
 *
 *   <BrandProvider>            — fetches /api/brand once per session and
 *                                applies the org's primary colour to a CSS
 *                                custom property `--brand-primary` so any
 *                                Tailwind utility referencing it (or any
 *                                inline style) is instantly theme-aware.
 *
 *   useBrand()                 — read-only hook returning the current
 *                                org's {org_name, logo_url, brand_primary_color, ...}.
 *                                Falls back to LogBook defaults on first
 *                                load / failure.
 *
 *   <Page>                     — master page wrapper. Slots: title, subtitle,
 *                                breadcrumbs, actions, loading skeleton.
 *                                Replaces every page's hand-rolled top section
 *                                with one consistent structure.
 *
 *   <Skeleton.* />             — primitive shimmer blocks for "loading
 *                                state" rendering. Used inside <Page loading={true}>.
 *
 *   useUnsavedChangesGuard(d)  — when `d` is true, blocks in-app route
 *                                changes AND the browser's beforeunload with a
 *                                confirm prompt. Wires react-router v7's
 *                                `useBlocker` + a `window.onbeforeunload`
 *                                listener with one hook call.
 *
 * **White-label rule**: nothing in this file hardcodes the word "LogBook".
 * Branding always flows from BrandProvider → useBrand() → consumed by the
 * shell. LogBook is only the *fallback* (no logo_url set, no brand colour set).
 */
import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { Link, useBlocker } from "react-router-dom";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";
import { PageHeader } from "./UI";

// =========================================================================
// 1. BrandProvider + useBrand
// =========================================================================

// White-label rule (iter66): the shell must NEVER hardcode the LogBook
// brand. Defaults below are empty so a tenant whose org hasn't uploaded a
// logo or set a colour gets a neutral fallback (org name text + design-
// token primary colour) — not the LogBook PNG.
const DEFAULT_BRAND = Object.freeze({
  org_id: null,
  org_name: "",
  logo_url: null,
  brand_primary_color: null,
  brand_accent_color: null,
  plan: null,
});

const BrandCtx = createContext({ brand: DEFAULT_BRAND, refresh: async () => {} });

export function BrandProvider({ children }) {
  const { user } = useAuth();
  const [brand, setBrand] = useState(DEFAULT_BRAND);

  const fetchBrand = useCallback(async () => {
    if (!user) { setBrand(DEFAULT_BRAND); return; }
    try {
      const r = await api.get("/brand");
      // Preserve null/empty as null so the topbar can fall through to the
      // org-name <span> instead of rendering the LogBook PNG default.
      setBrand({
        ...DEFAULT_BRAND,
        ...r.data,
        org_name: r.data.org_name || "",
        logo_url: r.data.logo_url || null,
      });
    } catch {
      // Network / 401 / no-org — keep defaults silently.
      setBrand(DEFAULT_BRAND);
    }
  }, [user]);

  useEffect(() => { fetchBrand(); }, [fetchBrand]);

  // Push brand colour into the cascade as `--brand-primary`. Consumers
  // can reference it with `style={{ backgroundColor: 'var(--brand-primary)' }}`
  // OR via Tailwind arbitrary values (`bg-[color:var(--brand-primary)]`).
  useEffect(() => {
    const root = document.documentElement;
    if (brand.brand_primary_color) {
      root.style.setProperty("--brand-primary", brand.brand_primary_color);
    } else {
      root.style.removeProperty("--brand-primary");
    }
    if (brand.brand_accent_color) {
      root.style.setProperty("--brand-accent", brand.brand_accent_color);
    } else {
      root.style.removeProperty("--brand-accent");
    }
  }, [brand.brand_primary_color, brand.brand_accent_color]);

  // iter 73 — White-label browser tab title. Mirrors the same fallback
  // philosophy as the topbar (DEFAULT_BRAND above):
  //   • Logged out / no org loaded:   "LogBook Legal"     (platform default)
  //   • Org with full white-label set
  //     (logo_url AND brand colour):  "{org_name}"        (drops LogBook attribution)
  //   • Any other authed tenant:      "{org_name} · LogBook Legal"
  useEffect(() => {
    if (!brand.org_name) {
      document.title = "LogBook Legal";
      return;
    }
    const fullyBranded = !!(brand.logo_url && brand.brand_primary_color);
    document.title = fullyBranded
      ? brand.org_name
      : `${brand.org_name} · LogBook Legal`;
  }, [brand.org_name, brand.logo_url, brand.brand_primary_color]);

  const value = useMemo(() => {
    const fullyBranded = !!(brand.logo_url && brand.brand_primary_color);
    // iter 73.15 Phase H — white-label helper. When a tenant is fully
    // branded ($250/mo tier), tenant-facing copy that hard-codes
    // "LogBook Legal" leaks the platform brand. Consumers should read
    // `platformName` and swap "LogBook Legal" for "the platform" when
    // `fullyBranded` is true (or use the raw org name if they prefer).
    return {
      brand,
      refresh: fetchBrand,
      fullyBranded,
      platformName: fullyBranded ? "the platform" : "LogBook Legal",
      platformPossessive: fullyBranded ? "the platform's" : "LogBook's",
    };
  }, [brand, fetchBrand]);
  return (
    <BrandCtx.Provider value={value}>
      <PreviewEnvBadge />
      {children}
    </BrandCtx.Provider>
  );
}

export const useBrand = () => useContext(BrandCtx);

// -------------------------------------------------------------------------
// PreviewEnvBadge — iter 73.9
// -------------------------------------------------------------------------
// Muted amber "PREVIEW" tag pinned to the top-right corner in every
// non-production environment, sniffed from `REACT_APP_BACKEND_URL`. The
// point is defensive: after we started diverging preview vs. production
// URLs (per-tenant `custom_domain` in prod, `PUBLIC_APP_URL` override in
// preview, both pointing to real customer inboxes), staff running a
// demo or a data-migration test on the preview host could easily
// forget which env they're in and touch real customer data. A tiny
// always-visible tag closes that gap for the cost of ~15 lines and
// zero external calls.
//
// Renders NOTHING when the backend URL contains neither "preview" nor
// "localhost" — i.e. on production `app.logbooklegal.com` and any
// authenticated tenant `custom_domain` the badge is invisible.
//
// Positioned `fixed` top-right at z-40 so it sits above sidebars/topbars
// but below modals (modals typically use z-50+). `pointer-events-none`
// so it never intercepts clicks on the underlying UI.
function PreviewEnvBadge() {
  const backendUrl = (process.env.REACT_APP_BACKEND_URL || "").toLowerCase();
  const isNonProd = backendUrl.includes("preview") || backendUrl.includes("localhost");
  if (!isNonProd) return null;
  const label = backendUrl.includes("localhost") ? "LOCAL DEV" : "PREVIEW ENV";
  return (
    <div
      className="fixed top-2 right-2 z-40 pointer-events-none select-none"
      data-testid="preview-env-badge"
      aria-hidden="true"
    >
      <span className="inline-flex items-center gap-1.5 px-2 py-1 text-[10px] font-mono font-semibold tracking-wider uppercase rounded border border-amber-500/40 bg-amber-500/10 text-amber-700 dark:text-amber-300 backdrop-blur-sm shadow-sm">
        <span className="w-1.5 h-1.5 rounded-full bg-amber-500 animate-pulse" />
        {label}
      </span>
    </div>
  );
}


// =========================================================================
// 2. Skeleton primitives
// =========================================================================

const _shimmer = "animate-pulse bg-muted/60 rounded";

export const Skeleton = ({ className = "", style }) => (
  <div className={`${_shimmer} ${className}`} style={style} data-testid="skeleton" />
);

Skeleton.Line = ({ width = "100%", height = "0.875rem", className = "" }) => (
  <div className={`${_shimmer} ${className}`} style={{ width, height }} data-testid="skeleton-line" />
);

Skeleton.Card = ({ className = "" }) => (
  <div className={`surface p-4 space-y-3 ${className}`} data-testid="skeleton-card">
    <Skeleton.Line width="40%" height="0.75rem" />
    <Skeleton.Line width="100%" height="1.25rem" />
    <Skeleton.Line width="80%" />
  </div>
);

Skeleton.TableRow = ({ cols = 5 }) => (
  <tr data-testid="skeleton-table-row">
    {Array.from({ length: cols }).map((_, i) => (
      <td key={i} className="px-3 py-3">
        <Skeleton.Line width={i === 0 ? "60%" : "85%"} />
      </td>
    ))}
  </tr>
);

Skeleton.PageBody = () => (
  <div className="space-y-4" data-testid="skeleton-page-body">
    <Skeleton.Card />
    <Skeleton.Card />
    <Skeleton.Card />
  </div>
);


// =========================================================================
// 3. <Page> wrapper
// =========================================================================

/**
 * Standard page shell. Use it at the top of every page like:
 *
 *   <Page title="Jobs" breadcrumbs={[{label: 'Dashboard', to: '/'}, 'Jobs']}
 *         actions={<button>+ NEW JOB</button>}
 *         loading={!data}>
 *      {pageBody}
 *   </Page>
 *
 * - `breadcrumbs` accepts an array of `{label, to}` or plain strings (the
 *   last entry is rendered as plain text, others as router links).
 * - `loading` flips the body to a `<Skeleton.PageBody>` placeholder. Pass
 *   a custom skeleton via `loadingSkeleton` to match the page layout.
 * - `subtitle` shows above the title (small label-style).
 * - `actions` slot sits in the top-right of the header (buttons / filters).
 */
export function Page({
  title,
  subtitle,
  breadcrumbs,
  actions,
  loading = false,
  loadingSkeleton,
  children,
  testid,
}) {
  return (
    <div className="p-8 space-y-4" data-testid={testid || "page"}>
      {breadcrumbs && breadcrumbs.length > 0 && (
        <Breadcrumbs items={breadcrumbs} />
      )}
      <PageHeader title={title} subtitle={subtitle} actions={actions} />
      {loading ? (loadingSkeleton || <Skeleton.PageBody />) : children}
    </div>
  );
}

function Breadcrumbs({ items }) {
  return (
    <nav className="text-xs text-muted-foreground flex items-center gap-1.5" data-testid="page-breadcrumbs" aria-label="Breadcrumb">
      {items.map((it, i) => {
        const label = typeof it === "string" ? it : it.label;
        const to = typeof it === "string" ? null : it.to;
        const last = i === items.length - 1;
        return (
          <span key={i} className="flex items-center gap-1.5">
            {to && !last
              ? <Link to={to} className="hover:text-foreground">{label}</Link>
              : <span className={last ? "text-foreground" : ""}>{label}</span>}
            {!last && <span className="opacity-60">/</span>}
          </span>
        );
      })}
    </nav>
  );
}


// =========================================================================
// 4. useUnsavedChangesGuard
// =========================================================================

/**
 * Block both in-app route changes (via react-router useBlocker) AND the
 * browser-tab close / refresh (via beforeunload) when `dirty` is true.
 *
 * The in-app guard is a window.confirm prompt; the browser one is the
 * standard `beforeunload` event so the user sees Chrome/Safari's
 * "Changes you made may not be saved" modal.
 *
 *   const dirty = form.touched && !saving;
 *   useUnsavedChangesGuard(dirty);
 */
export function useUnsavedChangesGuard(dirty) {
  const blocker = useBlocker(
    ({ currentLocation, nextLocation }) =>
      dirty && currentLocation.pathname !== nextLocation.pathname,
  );

  useEffect(() => {
    if (blocker.state === "blocked") {
      const proceed = window.confirm("You have unsaved changes. Leave anyway?");
      if (proceed) blocker.proceed?.();
      else blocker.reset?.();
    }
  }, [blocker]);

  useEffect(() => {
    if (!dirty) return;
    const onBeforeUnload = (e) => {
      e.preventDefault();
      // Chrome / Edge / Safari ignore the returnValue text but require it set.
      e.returnValue = "";
      return "";
    };
    window.addEventListener("beforeunload", onBeforeUnload);
    return () => window.removeEventListener("beforeunload", onBeforeUnload);
  }, [dirty]);
}
